import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import StatusPill from "@/components/StatusPill";
import { formatDateTime, STATUS_LABEL } from "@/lib/utils";
import { USER } from "@/constants/testIds";

const STATUSES = Object.keys(STATUS_LABEL);

export default function JobListPage() {
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const url = status ? `/jobs?status=${status}` : "/jobs";
    api.get(url).then(({ data }) => setJobs(data));
  }, [status]);

  const filtered = (jobs || []).filter((j) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      j.job_code?.toLowerCase().includes(s) ||
      j.project_name?.toLowerCase().includes(s) ||
      j.module_name?.toLowerCase().includes(s)
    );
  });

  if (!jobs) return <LoadingScreen />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex-1 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="jobs-search"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by job code or project…"
              className="w-full border border-gray-300 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
            />
          </div>
          <select
            data-testid="jobs-status-filter"
            value={status} onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0B6936]/20 focus:border-[#0B6936]"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
        <Link to="/app/jobs/new" data-testid={USER.newJobBtn}
          className="inline-flex items-center gap-2 rounded-md bg-[#0B6936] px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-[#07522f]">
          <Plus size={14} /> New job
        </Link>
      </div>

      <div className="sqit-card overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-14 text-center text-gray-500">No jobs match the current filters.</div>
        ) : (
          <table className="w-full text-sm" data-testid="jobs-table">
            <thead className="bg-gray-50 border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Job</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Module</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Samples</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Credits</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((j) => (
                <tr key={j.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <Link to={`/app/jobs/${j.id}`} className="font-semibold text-gray-900 transition-colors hover:text-[#0B6936]">
                      {j.job_code}
                    </Link>
                    <div className="text-xs text-gray-500">{j.project_name}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-700">{j.module_name}</td>
                  <td className="px-6 py-4 text-gray-700">{j.number_of_samples}</td>
                  <td className="px-6 py-4 text-gray-700">{j.credits_required}</td>
                  <td className="px-6 py-4"><StatusPill status={j.status} /></td>
                  <td className="px-6 py-4 text-gray-500 text-xs">{formatDateTime(j.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
